/**
 * Translation Utilities
 *
 * This file provides utility functions for extracting text from complex objects,
 * translating the extracted text, and applying translations back to the original
 * object structure.
 *
 * Implementation Logic:
 * 1. Extract all translatable text from an object structure, recording the path to each text
 * 2. Send the extracted texts to a translation service
 * 3. Apply the translated texts back to the original object structure using the recorded paths
 *
 * Example Process:
 * Given a review object:
 * {
 *   title: "Great Hotel",
 *   review: "We had a wonderful stay.",
 *   rating: 5,
 *   replies: [{ text: "Thank you!" }]
 * }
 *
 * Step 1: Extract texts and paths
 * - texts: ["Great Hotel", "We had a wonderful stay.", "Thank you!"]
 * - paths: [
 *     { path: "title", index: 0 },
 *     { path: "review", index: 1 },
 *     { path: "replies[0].text", index: 2 }
 *   ]
 *
 * Step 2: Translate texts
 * - translated: ["Excelente Hotel", "Tuvimos una estancia maravillosa.", "¡Gracias!"]
 *
 * Step 3: Apply translations using paths
 * Result:
 * {
 *   title: "Excelente Hotel",
 *   review: "Tuvimos una estancia maravillosa.",
 *   rating: 5,
 *   replies: [{ text: "¡Gracias!" }]
 * }
 *
 * The utilities handle complex nested objects and arrays, preserving the original
 * structure while replacing only the text content.
 */

/* eslint-disable no-param-reassign */
export type PathMapping = { path: string; index: number };

export const extractTextsFromObject = (
  obj: any,
  excludedFields: string[] = [],
): { texts: string[]; pathMap: PathMapping[] } => {
  const texts: string[] = [];
  const pathMap: PathMapping[] = [];

  const extractTextFromValue = (value: any, path: string) => {
    if (value === null || value === undefined) return;

    const type = typeof value;

    if (type === 'string' && value.trim()) {
      const fieldName = path.split('.').pop()?.split('[')[0] ?? '';
      if (!excludedFields.includes(fieldName)) {
        texts.push(value);
        pathMap.push({ path, index: texts.length - 1 });
      }
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const itemType = typeof item;
        const itemPath = path ? `${path}[${index}]` : `[${index}]`;

        if (itemType === 'string' && item.trim()) {
          texts.push(item);
          pathMap.push({ path: itemPath, index: texts.length - 1 });
        } else if (itemType === 'object' && item !== null) {
          extractTextFromValue(item, itemPath);
        }
      });
      return;
    }

    if (type === 'object') {
      Object.entries(value).forEach(([key, propValue]) => {
        if (!excludedFields.includes(key)) {
          const newPath = path ? `${path}.${key}` : key;
          extractTextFromValue(propValue, newPath);
        }
      });
    }
  };

  extractTextFromValue(obj, '');
  return { texts, pathMap };
};

export const parsePath = (part: string) => {
  const regex = /^(.+)\[(\d+)\]$/;
  const arrayMatch = regex.exec(part);

  if (arrayMatch) {
    const [, key, indexStr] = arrayMatch;
    const arrayIndex = parseInt(indexStr, 10);
    return { key, arrayIndex, isArray: true };
  }

  const pureArrayRegex = /^\[(\d+)\]$/;
  const pureArrayMatch = pureArrayRegex.exec(part);

  if (pureArrayMatch) {
    const [, indexStr] = pureArrayMatch;
    const arrayIndex = parseInt(indexStr, 10);
    return { key: '', arrayIndex, isArray: true };
  }

  return { key: part, arrayIndex: -1, isArray: false };
};

const handleArrayElement = (
  current: any,
  key: string,
  arrayIndex: number,
): any => {
  if (key === '') {
    const result = [...current];
    if (!result[arrayIndex]) result[arrayIndex] = {};
    return result[arrayIndex];
  }
  const result = { ...current };
  if (!result[key]) result[key] = [];
  if (!result[key][arrayIndex]) result[key][arrayIndex] = {};
  return result[key][arrayIndex];
};

const handleObjectProperty = (current: any, key: string): any => {
  const result = { ...current };
  if (!result[key]) result[key] = {};
  return result[key];
};

const setLastPathPart = (current: any, part: string, value: string): void => {
  const { arrayIndex, isArray, key } = parsePath(part);
  if (isArray) {
    if (key === '') {
      current[arrayIndex] = value;
    } else {
      if (!current[key]) current[key] = [];
      current[key][arrayIndex] = value;
    }
  } else {
    current[key] = value;
  }
};

export const setValueAtPath = (obj: any, path: string, value: string) => {
  const parts = path.split('.');
  let current = obj;
  const lastIndex = parts.length - 1;

  for (let i = 0; i < lastIndex; i += 1) {
    const { arrayIndex, isArray, key } = parsePath(parts[i]);
    current = isArray
      ? handleArrayElement(current, key, arrayIndex)
      : handleObjectProperty(current, key);
  }

  setLastPathPart(current, parts[lastIndex], value);
};

export const applyTranslationsToObject = (
  obj: any,
  pathMap: PathMapping[],
  translatedTexts: string[],
) => {
  const result = JSON.parse(JSON.stringify(obj));

  pathMap.forEach(({ index, path }) => {
    const translatedText = translatedTexts[index];

    if (!path) return;

    setValueAtPath(result, path, translatedText);
  });

  return result;
};
